import { makeAutoObservable, runInAction } from 'mobx';
import queryString from 'query-string';
import { apiServiceV2 } from '../../services/apiService';
import { CustomAxiosResponse } from '../../types/global.types';
import { parseSearchData, transformSearchData } from '../../helpers/utils';
import {
  CerebrumCategoryKey,
  cerebrumCategoryMap,
  TCategory,
} from './cerebrum.type';
import { globalStore } from '../../global.store';

const sortByInit = [
  {
    title: 'all',
    name: 'All',
    id: '',
    checked: true,
    onClick(setSearchParams: Function) {
      setSearchParams((searchParams: any) => {
        searchParams.delete('globalFilter');
        this.checked = true;
        return searchParams;
      });
    },
  },
  {
    title: 'last',
    name: 'Last Created',
    id: 'last_created',
    checked: false,
    onClick(setSearchParams: Function) {
      setSearchParams((searchParams: any) => {
        searchParams.set('globalFilter', 'last_created');
        this.checked = true;
        return searchParams;
      });
    },
  },
  {
    title: 'popular',
    name: 'Most Popular',
    id: 'popular',
    checked: false,
    onClick(setSearchParams: Function) {
      setSearchParams((searchParams: any) => {
        searchParams.set('globalFilter', 'popular');
        this.checked = true;
        return searchParams;
      });
    },
  },
];

class Cerebrum {
  categories: TCategory[] = [
    {
      title: 'world',
      name: 'World',
      categoryName: 'Country',
      id: 'ccc',
      onClick: (setSearchParams: Function, searchParams: URLSearchParams) => {
        this.setRoomInfo([]);
        cerebrumStore.removeState();
        cerebrumStore.removeChecks(setSearchParams, searchParams, 'world');
      },
    },
    {
      title: 'brand',
      name: 'Brands',
      categoryName: 'Brands',
      id: 'bbb',
      onClick: (setSearchParams: Function, searchParams: URLSearchParams) => {
        this.setRoomInfo([]);
        cerebrumStore.removeState();
        cerebrumStore.removeChecks(setSearchParams, searchParams, 'brand');
      },
    },
    {
      title: 'gaming',
      name: 'Gaming',
      categoryName: 'Gaming',
      id: 26,
      onClick: (setSearchParams: Function, searchParams: URLSearchParams) => {
        this.setRoomInfo([]);
        cerebrumStore.removeState();
        cerebrumStore.removeChecks(setSearchParams, searchParams, 'gaming');
      },
    },
  ];

  sortByList: {
    title: string;
    name: string;
    id: string | number;
    checked: boolean;
  }[] = sortByInit;

  filters: any = {
    'Sort By': this.sortByList,
  };

  countries: any = {};

  brands: any = {};

  gaming: any = {};

  showMap = false;

  listModeActive: boolean;

  roomInfo: any = [];

  searchValue = '';

  showFilters = false;

  scrollLoading = false;

  page = 1;

  pageCount = 0;

  perPage = 0;

  isDataLoading = true;

  // showFilterDropdown: boolean = true;
  filterDropdownState: { [key: string]: boolean } = {};

  constructor() {
    makeAutoObservable(this);
  }

  get renderItems() {
    const initData = {
      firstEls: [],
      secondEls: [],
    };

    return this.roomInfo.reduce(
      (accumulator: any, currentValue: any, index: number) => {
        if (index < (this.listModeActive ? 3 : 4)) {
          accumulator.firstEls.push(currentValue);
        } else {
          accumulator.secondEls.push(currentValue);
        }
        return accumulator;
      },
      initData,
    );
  }

  toggleDropdown = (filterTitle: string) => {
    this.filterDropdownState[filterTitle] =
      !this.filterDropdownState[filterTitle];
  };

  setSearchValue = (searchValue: string) => {
    this.searchValue = searchValue;
  };

  setShowMap = (state: boolean) => {
    this.showMap = state;
  };

  setRoomInfo = (data: any) => {
    this.roomInfo = data;
  };

  setPage = (page: number) => {
    this.page = page;
  };

  setShowFilters = (state: boolean) => {
    this.showFilters = state;
  };

  setListModeActive = (bool?: boolean) => {
    if (bool !== undefined) {
      this.listModeActive = bool;
    } else {
      this.listModeActive = !this.listModeActive;
    }
  };

  getCountries = async (params: any) => {
    const countries: CustomAxiosResponse = await apiServiceV2.getCountries({
      type: '',
    });
    const res: { [key in string]: any } = {};

    countries.data.forEach((country: any) => {
      res[country.name] = {
        ...country,
        checked: params.has('country')
          ? params
              .get('country')
              .split(',')
              .some((value: string) => value == country.id)
          : false,
        openedDropdown: false,
        listType: 'cities',
        onClick(setSearchParams: Function, searchParams: any) {
          const checkedCountry = searchParams.has('country')
            ? searchParams
                .get('country')
                .split(',')
                .some((value: string) => value == country.id)
            : false;

          const checkedCountries: any[] = Object.values(
            cerebrumStore.filters.Country,
          )
            .filter((el: any) => el.checked)
            .filter((value: any) => value !== country.id);
          const checkedCities = new Set([
            ...checkedCountries
              .map((country: any) =>
                country.cities
                  .reduce((prev: any, next: any) => [...prev, next.id], [])
                  .flat(),
              )
              .flat(),
          ]);
          const checkedCountriesId = new Set([
            ...checkedCountries
              .reduce((prev: any, next: any) => [...prev, next.id], [])
              .flat(),
          ]);
          setSearchParams(() => {
            if (checkedCountry) {
              this.checked = false;
              if (searchParams.get('country').split(',').length > 1) {
                searchParams.set(
                  'country',
                  searchParams
                    .get('country')
                    .split(',')
                    .filter((value: string) => value != this.id),
                );
              } else searchParams.delete('country');
              this.data = Object.values(this.data).reduce(
                (prev: any, next: any) => {
                  checkedCities.delete(next.id);
                  return {
                    ...prev,
                    [next.name]: {
                      ...next,
                      checked: false,
                    },
                  };
                },
                {},
              );
              if (Array.from(checkedCities).length) {
                searchParams.set('cities', Array.from(checkedCities));
              } else searchParams.delete('cities');
            } else {
              this.checked = true;
              const getCheckedCities = new Set(checkedCities);
              this.data = Object.values(this.data).reduce(
                (prev: any, next: any) => {
                  getCheckedCities.add(next.id);
                  return {
                    ...prev,
                    [next.name]: {
                      ...next,
                      checked: true,
                    },
                  };
                },
                {},
              );
              searchParams.set('cities', [...getCheckedCities]);
              searchParams.set('country', [
                ...Array.from(new Set([...checkedCountriesId, this.id])),
              ]);
            }
            return searchParams;
          });
        },
        data: country.cities.reduce(
          (prev: any, next: any) => ({
            ...prev,
            [next.name]: {
              ...next,
              checked: params.has('cities')
                ? params
                    .get('cities')
                    .split(',')
                    .some((city: string) => city == next.id)
                : false,
              onClick(setSearchParams: Function, searchParams: any) {
                setSearchParams(() => {
                  const cities = searchParams.has('cities')
                    ? searchParams.get('cities').split(',')
                    : [];
                  const checkedCountries = Object.values(
                    cerebrumStore.filters.Country,
                  )
                    .filter((el: any) => el.checked)
                    .filter((value: any) => value !== country.id);
                  if (searchParams.has('cities')) {
                    if (cities.includes(`${next.id}`)) {
                      this.checked = false;
                      if (cities.length > 1) {
                        searchParams.set(
                          'cities',
                          cities.filter((value: string) => value != next.id),
                        );
                      } else searchParams.delete('cities');
                      if (
                        !Object.values(
                          cerebrumStore.filters.Country[country.name].data,
                        ).every((value: any) => value.checked)
                      ) {
                        cerebrumStore.filters.Country[country.name].checked =
                          false;
                        if (checkedCountries.length > 1) {
                          searchParams.set(
                            'country',
                            searchParams
                              .get('country')
                              .split(',')
                              .filter(
                                (countryId: any) =>
                                  countryId !=
                                  cerebrumStore.filters.Country[country.name]
                                    .id,
                              ),
                          );
                        } else searchParams.delete('country');
                      }
                    } else {
                      this.checked = true;
                      searchParams.set(
                        'cities',
                        Array.from(new Set([...cities, `${next.id}`])),
                      );
                      if (
                        Object.values(
                          cerebrumStore.filters.Country[country.name].data,
                        ).every((value: any) => value.checked)
                      ) {
                        cerebrumStore.filters.Country[country.name].checked =
                          true;
                        searchParams.set('country', [
                          ...Array.from(
                            new Set([
                              ...checkedCountries.map(
                                (country: any) => country.id,
                              ),
                              `${
                                cerebrumStore.filters.Country[country.name].id
                              }`,
                            ]),
                          ),
                        ]);
                      }
                    }
                  } else {
                    this.checked = true;
                    searchParams.set('cities', [`${next.id}`]);
                    if (
                      Object.values(
                        cerebrumStore.filters.Country[country.name].data,
                      ).every((value: any) => value.checked)
                    ) {
                      cerebrumStore.filters.Country[country.name].checked =
                        true;
                      searchParams.set('country', [
                        ...Array.from(
                          new Set([
                            ...checkedCountries.map(
                              (country: any) => country.id,
                            ),
                            `${cerebrumStore.filters.Country[country.name].id}`,
                          ]),
                        ),
                      ]);
                    }
                  }
                  return searchParams;
                });
              },
            },
          }),
          {},
        ),
      };
    });
    this.setCountries(res);
  };

  getBrands = async (params: any) => {
    const brands: CustomAxiosResponse = await apiServiceV2.getBrands(
      '1',
      false,
    );
    const res: any[] = [];

    brands.data.forEach((brand: any) => {
      res.push({
        ...brand,
        checked: params.has('brand')
          ? params
              .get('brand')
              .split(',')
              .some((value: string) => value == brand.id)
          : false,
        onClick(setSearchParams: Function, searchParams: any) {
          this.checked = !this.checked;
          setSearchParams(() => {
            const checkedBrand = searchParams.has('brand')
              ? searchParams.get('brand').split(',')
              : [];
            if (checkedBrand.includes(`${this.id}`)) {
              if (checkedBrand.length === 1) {
                searchParams.delete('brand');
              } else {
                searchParams.set(
                  'brand',
                  checkedBrand.filter((value: string) => value != this.id),
                );
              }
            } else {
              searchParams.set('brand', [...checkedBrand, `${this.id}`]);
            }
            return searchParams;
          });
        },
      });
    });
    this.setBrands(res);
  };

  getGaming = async (params: any) => {
    const gaming: CustomAxiosResponse = await apiServiceV2.getBrands('1', true);
    const res: any[] = [];

    gaming.data.forEach((game: any) => {
      res.push({
        ...game,
        checked: params.has('brand')
          ? params
              .get('brand')
              .split(',')
              .some((value: string) => value == game.id)
          : false,
        onClick(setSearchParams: Function, searchParams: any) {
          this.checked = !this.checked;
          setSearchParams(() => {
            const checkedBrand = searchParams.has('brand')
              ? searchParams.get('brand').split(',')
              : [];
            if (checkedBrand.includes(`${this.id}`)) {
              if (checkedBrand.length === 1) {
                searchParams.delete('brand');
              } else {
                searchParams.set(
                  'brand',
                  checkedBrand.filter((value: string) => value != this.id),
                );
              }
            } else {
              searchParams.set('brand', [...checkedBrand, `${this.id}`]);
            }
            return searchParams;
          });
        },
      });
    });
    this.setGaming(res);
  };

  getRooms = async (searchParams: any) => {
    this.isDataLoading = true;

    try {
      const result = await apiServiceV2.getRooms(
        transformSearchData(searchParams),
      );
      runInAction(() => {
        this.setPage(2);
        this.setRoomInfo(result.data.items);
        this.pageCount = result.data.pageCount;
        this.perPage = result.data.perPage;
        this.setScrollLoading(false);
        this.isDataLoading = false;
      });
    } catch (error) {
      globalStore.setToasts(true, 'message_sent_error');
    }
  };

  setScrollLoading = (status: boolean) => {
    this.scrollLoading = status;
  };

  infinityLoading = async (params: any) => {
    if (this.perPage > this.pageCount) return;
    this.isDataLoading = true;
    this.setScrollLoading(true);
    const response = await apiServiceV2.getRooms(
      parseSearchData(params, this.page),
    );
    if (!response.error) {
      runInAction(() => {
        this.setRoomInfo(this.roomInfo.concat(response.data.items));
        this.setPage(response.data.page + 1);
        this.pageCount = response.data.pageCount;
        this.perPage = response.data.perPage;
        this.isDataLoading = false;
      });
      this.setScrollLoading(false);
    }
  };

  setCountries = (data: any) => {
    this.countries = data;
    this.filters = {
      ...this.filters,
      Country: data,
    };
  };

  setBrands = (data: any) => {
    this.brands = data;
    this.filters = {
      ...this.filters,
      Brands: data,
    };
  };

  setGaming = (data: any) => {
    this.gaming = data;
    this.filters = {
      ...this.filters,
      Gaming: data,
    };
  };

  setFilters = async (searchParams: any, category?: CerebrumCategoryKey) => {
    const data = {
      world: this.getCountries,
      brand: this.getBrands,
      gaming: this.getGaming,
    };

    if (!this.filters[cerebrumCategoryMap[category as CerebrumCategoryKey]]) {
      await data[category as CerebrumCategoryKey](searchParams);
    }
    if ((searchParams.get('search') || '') !== this.searchValue) {
      this.setSearchValue(searchParams.get('search'));
    }
  };

  deleteAction = (key: any, params: any) => {
    if (key !== 'listModeActive') {
      params.delete(key);
    }
  };

  removeAllChecks = () => {
    this.setListModeActive(false);
    const categories = ['world', 'brand', 'gaming'];
    categories.forEach((category: string) => this.removeChecksLocal(category));
  };

  removeChecksLocal = (category?: string) => {
    if (category === 'world') {
      const country =
        this.filters[cerebrumCategoryMap[category as CerebrumCategoryKey]];
      country &&
        Object.keys(country).forEach((countryName: string) => {
          country[countryName].checked = false;
          const city = country[countryName].data;
          Object.keys(city).forEach((cityName: string) => {
            city[cityName].checked = false;
          });
        });
    } else {
      const brands =
        this.filters[cerebrumCategoryMap[category as CerebrumCategoryKey]];
      if (brands) {
        this.filters[cerebrumCategoryMap[category as CerebrumCategoryKey]] =
          brands.map((item: any) => {
            item.checked = false;
            return item;
          });
      }
    }
  };

  removeChecks = async (
    setSearchParams: Function,
    searchParams: any,
    category?: string,
  ) => {
    if (this.listModeActive === undefined) {
      this.setListModeActive(searchParams.has('listModeActive'));
    }
    setSearchParams((params: URLSearchParams) => {
      const keys = Object.keys(queryString.parse(params.toString()));
      for (let i = 0; i < keys.length; i += 1) {
        this.deleteAction(keys[i], params);
      }

      for (const [key] of params.entries()) {
        this.deleteAction(key, params);
      }
      this.removeChecksLocal(category);
      return params;
    });
  };

  likeRoom = async (id: number) => {
    if (!globalStore.isLogin) return;
    const res = await apiServiceV2.handleLikeRoom(id);
    if (!res.error) {
      this.setRoomInfo(this.roomInfo.map((card: any) => {
          if (card.id === id) {
            const { isLiked } = res.data;
            card.isLiked = isLiked === 1;
            card.likesTotalCount += isLiked === 1 ? 1 : -1;
          }
          return card;
        }));
    } else {
      globalStore.setToasts(true, 'something_went_wrong');
    }
  };

  removeState = () => {
    this.filters = {};
  };
}

export const cerebrumStore = new Cerebrum();
